import React, { ReactNode, createContext, useState } from "react";

import { Stack, useTheme, useMediaQuery, SxProps } from "@mui/material";

interface SettingsOptionsProps<T> {
  /**
   * List of UI components that display options:
   * Accordion, Checkbox, Slider, Autocomplete, etc
   */
  children?: ReactNode;
  /**
   * Receieves object as the long argument which respresents the updated state object.
   * Then calls state setter with aforementioned object passed in as the argument.
   */
  // onChange: (newState: {}) => void;
  onChange: (newState: T) => void;
  /**
   * State object represented by series of key value pairs,
   * where the key is a string which represents the title of the
   * updatable settings option.
   */
  settingsState: T;

  useFullWidth?: boolean;

  sx?: SxProps;
}

function getDiff(defaultState: any, currentState: any): any {
  if (defaultState === currentState) {
    // If the defaultState is the same as the currentState, there are no changes needing to be saved.
    return null;
  }
  if (defaultState === null || currentState === null // "null" is an object in JavaScript
    || typeof defaultState !== "object" || typeof currentState !== "object") {
    // These are two primitives with a different value.  Return the currentState.
    return currentState;
  }

  // Start with the defaultState.  Make a "deep" copy to use.
  const diff: any = JSON.parse(JSON.stringify(defaultState));
  const allKeys = new Set([...Object.keys(defaultState), ...Object.keys(currentState)]);

  for (const key of allKeys) {
    if (!(key in currentState)) {
      // The currentState has no entry for this key.  If a value is being changed it should have a
      // valid entry.  This means there is no change from the default and thus the key doesn't 
      // need to be tracked.
      delete diff[key];
    } else if (!(key in defaultState)) {
      // This key is new.  It is possible there is no default set for this key.  That means this
      // change needs to be tracked.
      diff[key] = currentState[key];
    } else {
      const nestedDiff = getWidgetSettingsDelta(defaultState[key], currentState[key]);
      if (nestedDiff !== null) {
        diff[key] = nestedDiff;
      } else if (!nestedDiff) {
        delete diff[key];
      }
    }
  }

  return Object.keys(diff).length > 0 ? diff : null;
}

export function getWidgetSettingsDelta(defaultState: any, currentState: any): any {
  return getDiff(defaultState, currentState);
}

export const SettingsOptionsContext = createContext<{ updateSettingsState: (keyid: string, value: any) => void; } | null>(null);
const SettingsOptionsContextProvider = SettingsOptionsContext.Provider;

export default function SettingsOptions<T extends Record<string, any>>(props: SettingsOptionsProps<T>): React.JSX.Element {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [settingsState, setSettingsState] = useState<T>(props.settingsState);

  const updateSettingsState = (keyid: string, value: any) => {
    const newSettingsState: T = { ...settingsState, [keyid]: value };
    setSettingsState(newSettingsState);
    props.onChange(newSettingsState);
  }

  return (
    <SettingsOptionsContextProvider value={{ updateSettingsState }}>
      <Stack
        sx={{
          gap: 1,
          p: 0,
          width: isSmallScreen || props.useFullWidth ? "100%" : "50%",
          flex: 1,
          ...props.sx,
        }}>
        {props.children}
      </Stack>
    </SettingsOptionsContextProvider>
  );
}